import {
    Alert,
    Button,
    Container,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
    Title
} from "@mantine/core";
import {
    FILE_TYPE_OPTIONS,
    FileTypeEnum,
    FileUpload,
    generateVideoThumbnail,
    getFileTypeFromMimeType,
    PublicFileTypeEnum,
    showErrorNotification,
    showSuccessNotification,
    useCreatePublicFileRecordMutation,
    useCreatePublicUploadFilePresignedUrlMutation
} from "@therapprove/common";
import { useEffect, useMemo, useState } from "react";
import { Dialog } from "../components/Dialog";
import { uploadFilesPublicBase64ToPayload } from "../utils/payload.utils";

const PRIMARY_COLOR = "var(--mantine-color-primary-9)";

type FormFileEntry = {
    file: File;
    url: string;
    thumbnailUrl?: string;
    name: string;
    fileType: FileTypeEnum;
};

export const UpoadFilesPublicPage = () => {
    // Get the 'key' query string parameter from the URL.
    const urlParams = new URLSearchParams(window.location.search);
    const rawKey = urlParams.get("key");

    // State
    const [pageState, setPageState] = useState<"error" | "success" | null>(
        null
    );
    const [formFiles, setFormFiles] = useState<FormFileEntry[]>([]);
    const [isShared, setIsShared] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Mutations
    const [, createPresignedUrl] =
        useCreatePublicUploadFilePresignedUrlMutation();
    const [, createPublicFileRecord] = useCreatePublicFileRecordMutation();

    // Decode the key (base64 often contains '+' which can be converted to spaces in querystrings)
    const decoded = useMemo(() => {
        if (!rawKey) return undefined;
        const normalized = rawKey.replace(/ /g, "+");
        const payload = uploadFilesPublicBase64ToPayload(normalized);
        if (!payload) return undefined;
        return payload;
    }, [rawKey]);

    const organizationName = decoded?.organizationName;
    const key1 = decoded?.key1;
    const key2 = decoded?.key2;

    // Cleanup URLs
    useEffect(() => {
        return () => {
            formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Error state.
    if (!rawKey || !decoded || !key1 || !key2 || pageState === "error") {
        return (
            <Dialog
                title="Invalid Link"
                description=""
                failureMessage="Sorry, that link is invalid. Please contact the organization to get another link, or contact support@therapprove.com if you believe this is an error."
                icon="undo"
                showAgreement={false}
                isSuccessful={false}
            />
        );
    }

    // Success state.
    if (pageState === "success") {
        return (
            <Dialog
                title="Thank you!"
                description=""
                successMessage={`Your files were uploaded successfully${
                    organizationName ? ` for ${organizationName}` : ""
                }.`}
                icon="check"
                showAgreement={false}
                isSuccessful={true}
            />
        );
    }

    const hasFiles = formFiles.length > 0;

    const handleSetFiles = async (newFiles: File[]) => {
        const entries: FormFileEntry[] = await Promise.all(
            newFiles.map(async (file) => {
                let thumbnailUrl: string | undefined;
                if (file.type.startsWith("video/")) {
                    try {
                        thumbnailUrl = await generateVideoThumbnail(file);
                    } catch (e) {
                        console.warn("Could not generate video thumbnail", e);
                    }
                }

                return {
                    file,
                    url: URL.createObjectURL(file),
                    thumbnailUrl,
                    name: file.name
                        .replace(/\.[^/.]+$/, "")
                        .replace(/[^a-zA-Z0-9]/g, " "),
                    fileType: getFileTypeFromMimeType(file.type) as FileTypeEnum
                };
            })
        );

        setFormFiles((prev) => [...prev, ...entries]);
    };

    const removeFile = (index: number) => {
        setFormFiles((prev) => {
            const next = [...prev];
            URL.revokeObjectURL(next[index].url);
            next.splice(index, 1);
            return next;
        });
    };

    const updateFileField = (
        index: number,
        field: keyof FormFileEntry,
        value: any
    ) => {
        setFormFiles((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const clearAll = () => {
        formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        setFormFiles([]);
    };

    const uploadAll = async () => {
        if (!hasFiles) {
            showErrorNotification("Please select at least one file to upload");
            return;
        }

        setIsUploading(true);
        try {
            await Promise.all(
                formFiles.map(async (f) => {
                    const presignResult = await createPresignedUrl({
                        input: {
                            key1,
                            key2,
                            fileName: f.file.name,
                            fileType:
                                f.fileType as unknown as PublicFileTypeEnum,
                            contentType: f.file.type
                        }
                    });

                    const presigned = (presignResult.data as any)?.files
                        ?.createPublicUploadFilePresignedUrl;
                    const presignedUrl = presigned?.presignedUrl as
                        | string
                        | undefined;
                    const caregiverId = presigned?.caregiverId as
                        | string
                        | undefined;
                    const patientId = presigned?.patientId as
                        | string
                        | undefined;

                    if (!presignedUrl) {
                        throw new Error(
                            (presignResult.error as any)?.message?.replace?.(
                                "[GraphQL]",
                                ""
                            ) || "Failed to get upload URL"
                        );
                    }

                    const uploadResponse = await fetch(presignedUrl, {
                        method: "PUT",
                        body: f.file,
                        headers: { "Content-Type": f.file.type }
                    });

                    if (!uploadResponse.ok) {
                        throw new Error(
                            `File upload failed: ${uploadResponse.statusText}`
                        );
                    }

                    const recordResult = await createPublicFileRecord({
                        input: {
                            key1,
                            key2,
                            name: f.name || f.file.name,
                            fileName: f.file.name,
                            fileType:
                                f.fileType as unknown as PublicFileTypeEnum,
                            contentType: f.file.type,
                            isShared,
                            caregiverId,
                            patientId
                        }
                    });

                    const record = (recordResult.data as any)?.files
                        ?.createPublicFileRecord;

                    if (!record?.success) {
                        throw new Error(
                            record?.error || "Failed to create file record"
                        );
                    }
                })
            );

            showSuccessNotification(
                `${formFiles.length} file(s) uploaded successfully`
            );
            clearAll();
            setPageState("success");
        } catch (e) {
            const msg =
                e instanceof Error ? e.message : "Failed to upload files";
            showErrorNotification(msg);
            setPageState("error");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Container size="md" py="xl" style={{ maxWidth: "55rem" }}>
            <Paper shadow="sm" p="xl" radius="md">
                <Stack gap="lg">
                    <Stack gap="xs" align="center">
                        <Title order={2}>Upload Files</Title>
                        <Title order={4} lineClamp={1} c="dimmed" ta="center">
                            {organizationName
                                ? `Upload documents securely for ${organizationName}.`
                                : "Upload documents securely."}
                        </Title>
                    </Stack>

                    <Alert
                        color="primary"
                        variant="light"
                        style={{ borderColor: PRIMARY_COLOR }}
                    >
                        <Text variant="body" size="sm">
                            Your files are uploaded securely. Please do not
                            upload passwords or other sensitive credentials.
                        </Text>
                    </Alert>

                    <FileUpload
                        files={formFiles}
                        setFiles={handleSetFiles}
                        onRemove={removeFile}
                        onUpdate={updateFileField}
                        fileTypeOptions={FILE_TYPE_OPTIONS}
                    />

                    {hasFiles && (
                        <Group justify="space-between">
                            <Text variant="body-muted">
                                {hasFiles
                                    ? `${formFiles.length} file(s) selected`
                                    : "No files selected"}
                            </Text>
                            <Button
                                variant="subtle"
                                color="gray"
                                onClick={clearAll}
                            >
                                Clear all {formFiles.length} files
                            </Button>
                        </Group>
                    )}

                    {/* <TagsInput
            label="Tags (optional)"
            placeholder="Add tags to these files..."
            clearable
            acceptValueOnBlur
            splitChars={[","]}
            value={tags}
            onChange={setTags}
          /> */}
                    <Group justify="flex-end">
                        <Button
                            onClick={uploadAll}
                            disabled={!hasFiles || isUploading}
                            leftSection={
                                isUploading ? <Loader size={16} /> : undefined
                            }
                        >
                            {isUploading
                                ? "Uploading..."
                                : `Upload${hasFiles ? ` ${formFiles.length}` : ""}`}
                        </Button>
                    </Group>
                </Stack>
            </Paper>
        </Container>
    );
};
